/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Reading-time estimation core for kennisbank articles — plain ESM JavaScript
 * on purpose (the deepMerge.js pattern), because it has three consumers that
 * cannot share TypeScript:
 *   1. the client, via the typed facade src/lib/readingTime.ts
 *   2. the server (server/routes/content.ts adds readingMinutes to
 *      /api/articles) — tsconfig.server.json includes this file, allowJs
 *   3. scripts/prerender.mjs (plain ESM, cannot import TS) for the SSR
 *      article-index seed
 * Keep it dependency-free. Behavior must stay in lockstep with the block
 * model in src/pages/admin/articleTypes.ts.
 *
 * Walks every text-bearing field of the block model so callouts, FAQs,
 * tables, quotes and image captions all contribute. Strips HTML tags before
 * counting because rich blocks store markup. Words per minute differ by
 * language — Dutch silent reading is slightly slower than English.
 */

/** @type {Record<string, number>} */
const WORDS_PER_MINUTE = {
  nl: 200,
  en: 230,
};

/** @param {string} input */
const stripHtml = (input) =>
  input
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&[a-z]+;/gi, ' ');

/** @param {string} text */
const countWords = (text) => {
  const clean = stripHtml(text).trim();
  if (!clean) return 0;
  return clean.split(/\s+/).length;
};

/**
 * Word count for one content block. Dispatches on `kind` exactly like the
 * type guards in articleTypes.ts: a missing kind means a legacy rich block,
 * an unknown kind counts zero words.
 * @param {any} block
 * @returns {number}
 */
const blockWordCount = (block) => {
  switch (block?.kind) {
    case 'divider':
      return 0;
    case 'image':
      return countWords(block.caption ?? '') + countWords(block.alt ?? '');
    case 'quote':
      return countWords(block.text ?? '') + countWords(block.attribution ?? '');
    case 'callout':
      return countWords(block.heading ?? '') + countWords(block.text ?? '');
    case 'faq':
      return (
        countWords(block.heading ?? '') +
        (Array.isArray(block.items)
          ? /** @type {Array<{ q?: string, a?: string }>} */ (block.items).reduce(
              (sum, item) => sum + countWords(item.q ?? '') + countWords(item.a ?? ''),
              0
            )
          : 0)
      );
    case 'table':
      return (
        countWords(block.heading ?? '') +
        countWords(block.caption ?? '') +
        (Array.isArray(block.rows)
          ? /** @type {Array<Array<string | null | undefined>>} */ (block.rows).reduce(
              (sum, row) => sum + row.reduce((rowSum, cell) => rowSum + countWords(cell ?? ''), 0),
              0
            )
          : 0)
      );
    case undefined:
    case 'rich':
      return countWords(block?.heading ?? '') + countWords(block?.text ?? '');
    default:
      return 0;
  }
};

/**
 * @param {any[]} blocks
 * @returns {number}
 */
export function totalWordCount(blocks) {
  return blocks.reduce((sum, b) => sum + blockWordCount(b), 0);
}

/**
 * @param {any[]} blocks
 * @param {'nl'|'en'} language
 * @returns {number}
 */
export function estimateReadingMinutes(blocks, language) {
  const words = totalWordCount(blocks);
  if (words === 0) return 1;
  const wpm = WORDS_PER_MINUTE[language] || WORDS_PER_MINUTE.nl;
  return Math.max(1, Math.ceil(words / wpm));
}

/**
 * ISO 8601 duration for schema.org Article.timeRequired (e.g. "PT5M").
 * @param {number} minutes
 * @returns {string}
 */
export function readingTimeISO(minutes) {
  return `PT${Math.max(1, Math.round(minutes))}M`;
}
